import { isAxiosError } from 'axios';

/**
 * Pulls a human-readable message out of an API error.
 *
 * Handles axios errors carrying ASP.NET problem details ({ title, status, errors: { Field: [msg] } }),
 * plain { message } / string bodies, and plain Error instances; falls back to a caller-supplied
 * default or a status-based sentence. Field names inside validation messages are humanised
 * ("NumberOfCertifiedDOTProfessionals must be between 0 and 999" → "Number of certified DOT
 * professionals must be between 0 and 999.") so the user never sees raw JSON or
 * "Request failed with status code 400" (SLV2-2999 / SLV2-3195 DEV passes).
 */
export function getApiErrorMessage(error: unknown, fallback = 'Request failed'): string {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data as ProblemDetails | string | undefined;

    if (data && typeof data === 'object') {
      const fieldMessages = flattenValidationErrors(data.errors);
      if (fieldMessages.length > 0) {
        return fieldMessages.join(' ');
      }
      const serverMessage = firstNonEmpty(data.message, data.detail, data.title);
      if (serverMessage && !isGenericTitle(serverMessage)) {
        return serverMessage;
      }
    } else if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) {
      return data.trim();
    }

    if (!error.response) {
      // Timeout / offline / CORS: axios has no HTTP status to map.
      return error.code === 'ECONNABORTED'
        ? 'The request timed out. Please try again.'
        : 'Could not reach the server. Check your connection and try again.';
    }

    return statusFallback(status, fallback);
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

interface ProblemDetails {
  title?: string;
  detail?: string;
  message?: string;
  status?: number;
  errors?: Record<string, string[] | string>;
}

function flattenValidationErrors(errors: ProblemDetails['errors']): string[] {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return [];
  const out: string[] = [];
  for (const [field, raw] of Object.entries(errors)) {
    const label = humaniseFieldName(field);
    const messages = Array.isArray(raw) ? raw : [raw];
    for (const message of messages) {
      // Some controllers serialise ModelState as objects rather than strings; skip anything
      // that is not text rather than throwing inside a render.
      if (typeof message !== 'string' || !message) continue;
      // The server repeats the property name inside its message
      // ("NumberOfCertifiedDOTProfessionals must be between 0 and 999");
      // swap it for the readable label so the sentence reads naturally.
      const text = field ? message.split(field).join(label) : message;
      out.push(ensureSentence(text));
    }
  }
  return out;
}

/** "NumberOfCertifiedDOTProfessionals" → "Number of certified DOT professionals". */
export function humaniseFieldName(field: string): string {
  const name = field.includes('.') ? field.slice(field.lastIndexOf('.') + 1) : field;
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w, i) => {
      const isAcronym = w.length > 1 && w === w.toUpperCase();
      if (isAcronym) return w;
      const lower = w.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    });
  return words.join(' ');
}

function ensureSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  return /[.!?]$/.test(trimmed) ? trimmed : trimmed + '.';
}

function firstNonEmpty(...values: Array<string | undefined>): string | undefined {
  return values.find(v => typeof v === 'string' && v.trim().length > 0)?.trim();
}

/** ASP.NET's default problem titles say nothing a user can act on. */
function isGenericTitle(text: string): boolean {
  return /^one or more validation errors occurred\.?$/i.test(text)
    || /^bad request\.?$/i.test(text)
    || /^an error occurred while processing your request\.?$/i.test(text);
}

function statusFallback(status: number | undefined, fallback: string): string {
  switch (status) {
    case 400: return 'Some of the entered values are not valid. Please check the form and try again.';
    case 401: return 'Your session has expired. Please sign in again.';
    case 403: return "You don't have permission to make this change.";
    case 404: return 'The record could not be found. It may have been removed.';
    case 409: return 'This change conflicts with the current state of the record. Refresh and try again.';
    default:
      if (status !== undefined && status >= 500) {
        return 'The server ran into a problem saving your changes. Please try again in a moment.';
      }
      return fallback;
  }
}
